import { useTranslation } from "react-i18next";
import { translateError } from "@/lib/errors";
import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Play, Share2 } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { Card } from "@/components/primitives/Card";
import { StatusPill, type StatusTone } from "@/components/primitives/StatusPill";
import { fmtDuration, renderDownloadUrl, renderVideoUrl, type MontageRender } from "@/lib/montageApi";

const TONE: Record<MontageRender["status"], StatusTone> = {
  IN_ATTESA: "cyan",
  RENDERING: "cyan",
  COMPLETATO: "success",
  ERRORE: "danger",
};
const LABEL: Record<MontageRender["status"], string> = {
  IN_ATTESA: "render.statusPending",
  RENDERING: "render.rendering",
  COMPLETATO: "render.statusCompleted",
  ERRORE: "common.error",
};

interface Props {
  render: MontageRender | null;
  title: string;
  canRender: boolean;
  totalDuration: number;
  onStart: () => Promise<void>;
}

export function RenderPanel({ render, title, canRender, totalDuration, onStart }: Props) {
  const { t } = useTranslation();
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const active = render?.status === "IN_ATTESA" || render?.status === "RENDERING";
  const fileName = `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "montage"}.mp4`;

  const start = async () => {
    if (starting || active) return;
    setStarting(true);
    try {
      await onStart();
    } finally {
      setStarting(false);
    }
  };

  const download = async () => {
    if (!render) return;
    setDownloading(true);
    try {
      const resp = await fetch(renderDownloadUrl(render.id), { credentials: "include" });
      if (!resp.ok) throw new Error();
      const url = URL.createObjectURL(await resp.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("sceneVideo.downloadFailed"));
    } finally {
      setDownloading(false);
    }
  };

  const share = async () => {
    if (!render || sharing) return;
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (typeof nav.share !== "function" || typeof nav.canShare !== "function") {
      toast.info(t("render.shareUnavailable"));
      return;
    }
    if (!nav.canShare({ files: [new File([], fileName, { type: "video/mp4" })] })) {
      toast.info(t("render.shareUnavailable"));
      return;
    }
    setSharing(true);
    try {
      const resp = await fetch(renderDownloadUrl(render.id), { credentials: "include" });
      if (!resp.ok) throw new Error();
      const file = new File([await resp.blob()], fileName, { type: "video/mp4" });
      if (!nav.canShare({ files: [file] })) {
        toast.info(t("render.shareUnavailable"));
        return;
      }
      await nav.share({ files: [file], title });
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      toast.error(t("render.shareFailed"));
    } finally {
      setSharing(false);
    }
  };

  return (
    <Card data-testid="montage-render-panel" brackets className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-display text-sm font-semibold uppercase tracking-wide">{t("render.title")}</p>
          <p className="text-xs text-muted-foreground">
            {t("render.estimatedDuration")}: <span data-testid="montage-total-duration" className="text-foreground">{fmtDuration(totalDuration)}</span>
          </p>
        </div>
        {render && (
          <StatusPill data-testid="montage-render-status" tone={TONE[render.status]} pulse={active}>
            {t(LABEL[render.status])}
          </StatusPill>
        )}
      </div>

      <Button
        data-testid="montage-render-btn"
        onClick={start}
        disabled={!canRender || starting || active}
        className="w-full sm:w-auto"
      >
        {starting || active ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        {active ? t("render.inProgress") : t("render.create")}
      </Button>

      {render?.status === "RENDERING" && (
        <div data-testid="montage-render-progress" className="space-y-1.5">
          {render.render_phase === "STORING" ? (
            <p data-testid="montage-render-progress-label" className="text-xs text-muted-foreground">{t("render.saving")}</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 text-xs">
                <span data-testid="montage-render-progress-label" className="text-foreground">
                  {t("render.progressTitle", { pct: render.progress_pct ?? 0 })}
                </span>
                <span data-testid="montage-render-progress-time" className="text-muted-foreground">
                  {fmtDuration(render.progress_out_time_s ?? 0)} / {fmtDuration(render.expected_duration ?? totalDuration)}
                </span>
              </div>
              <div
                data-testid="montage-render-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={render.progress_pct ?? 0}
                className="h-1.5 w-full overflow-hidden rounded-full bg-primary/15"
              >
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-700"
                  style={{ width: `${Math.min(100, Math.max(0, render.progress_pct ?? 0))}%` }}
                />
              </div>
              {render.eta_seconds != null && (
                <p data-testid="montage-render-eta" className="text-xs text-muted-foreground">
                  {render.eta_seconds < 60
                    ? t("render.etaUnderMinute")
                    : t("render.etaRemaining", { min: Math.max(1, Math.round(render.eta_seconds / 60)) })}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {render?.status === "ERRORE" && (
        <p data-testid="montage-render-error" className="text-sm text-[#FF5C6C]">{translateError(render.error_message)}</p>
      )}

      {render?.status === "COMPLETATO" && (
        <div className="space-y-3">
          <video
            key={render.id}
            data-testid="montage-render-video"
            src={`${renderVideoUrl(render.id)}?v=${render.id}`}
            controls
            className="max-h-[420px] w-full rounded-md bg-black"
          />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {t("render.duration")}: <span data-testid="montage-render-duration" className="text-foreground">{fmtDuration(render.duration_seconds)}</span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button data-testid="montage-download-btn" variant="secondary" size="sm" onClick={download} disabled={downloading}>
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {t("common.downloadMp4")}
              </Button>
              <Button data-testid="montage-share-btn" variant="secondary" size="sm" onClick={share} disabled={sharing}>
                {sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
                {t("render.share")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
